import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import { CommonButton } from "./commonButton";
import CustomDateRangePicker from "./CustomDateRangePicker";
import "./commonPageStyles.css";

const FilterSidebar = styled(Box)({
  width: "280px",
  backgroundColor: "#ffffff",
  borderRight: "1px solid #e0e0e0",
  height: "calc(100vh - 180px)",
  maxHeight: "calc(100vh - 180px)",
  position: "sticky",
  top: "0",
  transition: "all 0.3s ease",
  boxShadow: "2px 0 8px rgba(0,0,0,0.05)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

const FilterScrollableContent = styled(Box)({
  flex: "0 1 auto",
  overflowY: "auto",
  overflowX: "hidden",
  padding: "20px",
  paddingBottom: "12px",
  position: "relative",
  zIndex: 1,
  minHeight: 0,
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#ffffff",
    borderRadius: "10px",
    "&:hover": {
      background: "#f5f5f5",
    },
  },
});

const FilterButtonsContainer = styled(Box)({
  padding: "12px 20px",
  borderTop: "1px solid #e0e0e0",
  backgroundColor: "#ffffff",
  flexShrink: 0,
});

const FilterSection = styled(Box)({
  marginBottom: "24px",
  position: "relative",
  zIndex: 1,
  "& .filter-title": {
    fontSize: "16px",
    fontWeight: "600",
    color: "#000",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
});

const CheckboxListContainer = styled(Box)({
  maxHeight: "200px",
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#ffffff",
    borderRadius: "10px",
  },
});

/**
 * CommonFilterSidebar - Reusable filter sidebar component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the sidebar is open
 * @param {Array} props.filterConfigs - Array of filter configurations
 * @param {Function} props.onApplyFilters - Callback when Apply Filters is clicked
 * @param {Function} props.onClearFilters - Callback when Clear All is clicked
 * @param {boolean} props.isLoading - Whether filters are being applied
 * @param {boolean} props.isFilterDisabled - Whether filter buttons should be disabled
 * @param {Object} props.dateRangeConfig - Configuration for date range picker (optional)
 */
export const CommonFilterSidebar = ({
  isOpen,
  filterConfigs = [],
  onApplyFilters,
  onClearFilters,
  isLoading = false,
  isFilterDisabled = false,
  dateRangeConfig = null,
}) => {
  const [localSearchValues, setLocalSearchValues] = useState({});

  useEffect(() => {
    const newSearchValues = {};
    filterConfigs.forEach(config => {
      // Use config.label as unique identifier since multiple filters can share the same type (e.g. "bankName")
      if (['bankName', 'accountNo', 'workflowName', 'initiatedBy'].includes(config.type)) {
        newSearchValues[config.label] = config.searchValue || "";
      }
    });
    setLocalSearchValues(prev => ({
      ...prev,
      ...newSearchValues
    }));
  }, [filterConfigs]);

  const handleSearchChange = (label, value, parentHandler) => {
    setLocalSearchValues(prev => ({
      ...prev,
      [label]: value
    }));
    if (parentHandler) {
      parentHandler(value);
    }
  };

  if (!isOpen) return null;

  return (
    <FilterSidebar>
      <FilterScrollableContent>
        <Box sx={{ marginBottom: "20px" }}>
          <Typography
            variant="h6"
            className="filter-header"
            sx={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#1672DE",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <FilterListIcon sx={{ fontSize: "22px" }} />
            Filters
          </Typography>
          <Typography
            variant="body2"
            className="filter-subtitle"
            sx={{ color: "#666", fontSize: "12px" }}
          >
            Refine your search
          </Typography>
        </Box>
        <Divider sx={{ marginBottom: "20px", borderColor: "#e0e0e0" }} />

        {filterConfigs.map((config, index) => {
          if (config.type === "bankName" || config.type === "accountNo" || config.type === "workflowName" || config.type === "initiatedBy") {
            return (
              <React.Fragment key={config.type || index}>
                <FilterSection>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#000",
                      marginBottom: "12px",
                    }}
                  >
                    {config.label}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={config.placeholder || `Search ${config.label.toLowerCase()}`}
                    value={localSearchValues[config.label] || ""}
                    onChange={(e) => handleSearchChange(config.label, e.target.value, config.onSearchChange)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#999", fontSize: "18px" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      marginBottom: "8px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        "& fieldset": {
                          borderColor: "#d0d0d0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#999",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1672DE",
                        },
                      },
                    }}
                  />
                  <Box sx={{ minHeight: "24px", marginBottom: "8px" }}>
                    <Typography
                      onClick={config.onClear}
                      sx={{
                        fontSize: "13px",
                        color: "#000",
                        cursor: "pointer",
                        textDecoration: "underline",
                        visibility: (config.selectedItems && config.selectedItems.length > 0) ? "visible" : "hidden",
                        display: "inline-block"
                      }}
                    >
                      &lt; Clear
                    </Typography>
                  </Box>
                  <CheckboxListContainer>
                    {(() => {
                      const items = config.items || [];
                      const searchValue = (localSearchValues[config.label] || "").toLowerCase().trim(); // Use local search value for filtering
                      const filteredItems = searchValue
                        ? items.filter((item) =>
                          String(item).toLowerCase().includes(searchValue)
                        )
                        : items;

                      if (filteredItems.length === 0) {
                        return (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#999",
                              fontStyle: "italic",
                              padding: "8px",
                            }}
                          >
                            {items.length === 0
                              ? "No options available"
                              : "No matching options"}
                          </Typography>
                        );
                      }

                      return filteredItems.map((item) => {
                        const itemStr = String(item);
                        const isSelected = (config.selectedItems || []).includes(itemStr);
                        const hasSelection = (config.selectedItems || []).length > 0;
                        const isDisabled = hasSelection && !isSelected && config.singleSelection;

                        return (
                          <FormControlLabel
                            key={itemStr}
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={() => config.onItemChange?.(itemStr)}
                                disabled={isDisabled}
                                sx={{
                                  color: "#1672DE",
                                  "&.Mui-checked": {
                                    color: "#1672DE",
                                  },
                                  "&.Mui-disabled": {
                                    color: "#ccc",
                                  },
                                  padding: "4px 9px",
                                }}
                              />
                            }
                            label={
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: isSelected ? "600" : "400",
                                  color: isDisabled ? "#ccc" : "#000",
                                }}
                              >
                                {itemStr}
                              </Typography>
                            }
                            sx={{ margin: 0, display: "flex", alignItems: "center" }}
                          />
                        );
                      });
                    })()}
                  </CheckboxListContainer>
                </FilterSection>
                {index < filterConfigs.length - 1 && (
                  <Divider sx={{ marginY: "20px", borderColor: "#e0e0e0" }} />
                )}
              </React.Fragment>
            );
          }

          if (config.type === "textInput") {
            return (
              <React.Fragment key={config.type || index}>
                <FilterSection>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#000",
                      marginBottom: "12px",
                    }}
                  >
                    {config.label}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type={config.inputType || "text"}
                    placeholder={config.placeholder || `Enter ${config.label.toLowerCase()}`}
                    value={config.value || ""}
                    onChange={(e) => config.onChange?.(e.target.value)}
                    onKeyUp={config.onKeyUp}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        "& fieldset": {
                          borderColor: "#d0d0d0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#999",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1672DE",
                        },
                      },
                    }}
                  />
                </FilterSection>
                {index < filterConfigs.length - 1 && (
                  <Divider sx={{ marginY: "20px", borderColor: "#e0e0e0" }} />
                )}
              </React.Fragment>
            );
          }

          if (config.type === "dateRange") {
            return (
              <React.Fragment key={config.type || index}>
                <FilterSection>
                  <Typography className="filter-title">{config.label || "Transaction Period"}</Typography>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      overflow: "visible",
                      padding: "4px 0",
                      "& .customDateRangeContainer": {
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: "100%",
                        padding: 0,
                        position: "relative",
                      },
                      "& .customDateRangeInputContainer": {
                        width: "100%",
                        position: "relative",
                      },
                      "& .customDateRangeInput": {
                        width: "100% !important",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                      },
                      "& .customDateRangeCard": {
                        position: "absolute",
                        zIndex: 1003,
                        left: 0,
                        right: 0,
                        transform: "none",
                        marginLeft: 0,
                        marginTop: "8px",
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        overflow: "hidden",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                        "& .rdrCalendarWrapper": {
                          width: "100%",
                          maxWidth: "100%",
                        },
                        "& .rdrDateRangePickerWrapper": {
                          width: "100%",
                          maxWidth: "100%",
                        },
                        "& .rdrMonth": {
                          width: "100%",
                          maxWidth: "100%",
                          padding: "8px",
                        },
                      },
                    }}
                    onKeyUp={config.onKeyUp}
                  >
                    <CustomDateRangePicker
                      startDate={config.startDate}
                      setStartDate={config.setStartDate}
                      endDate={config.endDate}
                      setEndDate={config.setEndDate}
                      maxDate={config.maxDate || new Date()}
                      customWidth="100%"
                    />
                  </Box>
                </FilterSection>
                {index < filterConfigs.length - 1 && (
                  <Divider sx={{ marginY: "20px", borderColor: "#e0e0e0" }} />
                )}
              </React.Fragment>
            );
          }

          if (config.type === "custom") {
            return (
              <React.Fragment key={config.type || index}>
                <FilterSection>
                  {config.content}
                </FilterSection>
                {index < filterConfigs.length - 1 && (
                  <Divider sx={{ marginY: "20px", borderColor: "#e0e0e0" }} />
                )}
              </React.Fragment>
            );
          }

          return null;
        })}
      </FilterScrollableContent>

      <FilterButtonsContainer>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <CommonButton
            text="Apply Filters"
            handleClick={onApplyFilters}
            hoverColor="#1565c0"
            disabled={isLoading || isFilterDisabled}
            style={{
              backgroundColor: "#1672DE",
              color: "white",
              width: "100%",
              borderRadius: "6px",
              padding: "10px",
              fontWeight: "500",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 4px rgba(22, 114, 222, 0.2)",
            }}
          />
          <CommonButton
            text="Clear All"
            handleClick={onClearFilters}
            hoverColor="#1565c0"
            disabled={isLoading}
            style={{
              backgroundColor: "transparent",
              color: "#1672DE",
              border: "1px solid #1672DE",
              width: "100%",
              borderRadius: "6px",
              padding: "10px",
              fontWeight: "500",
              transition: "all 0.3s ease",
            }}
          />
        </Box>
      </FilterButtonsContainer>
    </FilterSidebar>
  );
};

